import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import { createActionBar } from '../action-bar';
import { createNumberLine, type NumberLineHandle } from '../render/number-line';
import { createSvgEl } from '@learnforge/demo-render';
import type { ArithContext, ArithKey } from '../types';
import { defineExperiment, type CustomExperimentSpec } from '@learnforge/demo-render/harness';

export type LineOp = '+' | '-' | '*';

const OP_SYMBOL: Record<LineOp, string> = { '+': '+', '-': '−', '*': '×' };

export interface ArrowDescriptor {
  from: number;
  to: number;
  className: string;
  flip?: boolean;
}

// Operations as chained arrows on the number line. '+'/'-' are two arrows
// (0→a, then a→a±b); '*' is one arrow to the product, flipped if b is negative.
export function arrowFrames(op: LineOp, a: number, b: number): ArrowDescriptor[] {
  const cls = (from: number, to: number): string => (to >= from ? 'pos' : 'neg');
  if (op === '+') {
    return [
      { from: 0, to: a, className: cls(0, a) },
      { from: a, to: a + b, className: cls(a, a + b) },
    ];
  }
  if (op === '-') {
    return [
      { from: 0, to: a, className: cls(0, a) },
      { from: a, to: a - b, className: cls(a, a - b) },
    ];
  }
  const product = a * b;
  return [{ from: 0, to: product, className: cls(0, product), flip: b < 0 }];
}

// Multiplication shown as repeated addition: |b| chained copies of a, each a
// step of `a · sign(b)`. The step's sign carries the rule "two negatives make a
// positive" — e.g. -3 × -2 walks +3, +3 (up to +6), same shape as 3 × 2, while
// the operand dots sit at the negatives. Falls back to one arrow for b = 0 or
// a non-integer / very large |b|.
export function multiplicationArrows(a: number, b: number): ArrowDescriptor[] {
  if (Number.isInteger(b) && b !== 0 && Math.abs(b) <= 20) {
    const copies = Math.abs(b);
    const step = a * Math.sign(b);
    const out: ArrowDescriptor[] = [];
    let pos = 0;
    for (let k = 0; k < copies; k += 1) {
      out.push({ from: pos, to: pos + step, className: 'arrow-a' });
      pos += step;
    }
    return out;
  }
  return [{ from: 0, to: a * b, className: 'arrow-b' }];
}

export type Irrational = 'sqrt2' | 'pi' | 'e' | 'phi';

const IRRATIONAL_DIGITS: Record<Irrational, string> = {
  sqrt2: '1.41421356237309504880168872420969807856967187537694',
  pi: '3.14159265358979323846264338327950288419716939937510',
  e: '2.71828182845904523536028747135266249775724709369995',
  phi: '1.61803398874989484820458683436563811772030917980576',
};

// Returns the first `count` decimal digits (ignoring the radix point) of a known irrational.
export function decimalDigits(value: Irrational, count: number): number[] {
  const s = IRRATIONAL_DIGITS[value].replace('.', '');
  const digits: number[] = [];
  for (let i = 0; i < count && i < s.length; i += 1) {
    digits.push(Number(s[i]));
  }
  return digits;
}

export interface Convergent {
  num: number;
  den: number;
}

// Continued-fraction convergents of `value`, e.g. pi → 3/1, 22/7, 333/106, 355/113.
export function convergentsOf(value: number, depth: number): Convergent[] {
  const out: Convergent[] = [];
  let x = value;
  let h0 = 1;
  let h1 = Math.floor(x);
  let k0 = 0;
  let k1 = 1;
  out.push({ num: h1, den: k1 });
  for (let i = 1; i < depth; i += 1) {
    const frac = x - Math.floor(x);
    if (frac < 1e-12) break;
    x = 1 / frac;
    const a = Math.floor(x);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    out.push({ num: h2, den: k2 });
    h0 = h1;
    h1 = h2;
    k0 = k1;
    k1 = k2;
  }
  return out;
}

// Keeps 0, both operands and the result on screen with a little padding.
export function fitRange(values: readonly number[]): [number, number] {
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const pad = Math.max(1, Math.round((hi - lo) * 0.1));
  return [lo - pad, hi + pad];
}

function niceStep(width: number): number {
  if (width <= 20) return 1;
  if (width <= 40) return 2;
  if (width <= 100) return 5;
  return Math.max(10, Math.round(width / 20) * 5);
}

const NL_PRESETS: readonly { id: string; a: number; b: number; op: LineOp }[] = [
  { id: 'add-2-3', a: 2, b: 3, op: '+' },
  { id: 'sub-5-8', a: 5, b: 8, op: '-' },
  { id: 'mul-3-4', a: 3, b: 4, op: '*' },
  { id: 'neg3-times-neg2', a: -3, b: -2, op: '*' },
];

const ADVANCED_STORAGE_KEY = 'arithmetic.number-line-surgeon.advanced';

interface State {
  a: number;
  b: number;
  op: LineOp;
  advanced: boolean;
}

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.number-line-surgeon.label',
    demoId: 'arithmetic',
    advancedKey: 'number-line-surgeon',

    initialState(ctx): State {
      return {
        a: 2,
        b: 3,
        op: '+',
        advanced: ctx.advanced,
      };
    },

    applyPreset(state, presetId): State {
      const preset = NL_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return { ...state, a: preset.a, b: preset.b, op: preset.op };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage (built once) ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 250', class: 'arith-stage' });
      host.appendChild(svg);

      const defs = createSvgEl('defs', {});
      const marker = createSvgEl('marker', {
        id: 'arith-arrowhead',
        viewBox: '0 0 10 10',
        refX: '8',
        refY: '5',
        markerWidth: '6',
        markerHeight: '6',
        orient: 'auto-start-reverse',
      });
      const arrowPath = createSvgEl('path', {
        d: 'M 0 0 L 10 5 L 0 10 z',
        class: 'arith-arrowhead-fill',
      });
      marker.appendChild(arrowPath);
      defs.appendChild(marker);
      svg.appendChild(defs);

      const lineGroup = createSvgEl('g', { transform: 'translate(20, 20)' });
      svg.appendChild(lineGroup);

      const formula = createSvgEl('text', {
        class: 'arith-readout',
        x: 400,
        y: 224,
        'text-anchor': 'middle',
      });
      svg.appendChild(formula);

      // Transient closure var — NOT in State
      let numberLineHandle: NumberLineHandle | null = null;

      // ── Helper: compute result from state ──
      function compute(state: State): number {
        if (state.op === '+') return state.a + state.b;
        if (state.op === '-') return state.a - state.b;
        return state.a * state.b;
      }

      function buildArrows(state: State): ReturnType<typeof arrowFrames> {
        if (state.op === '*') return multiplicationArrows(state.a, state.b);
        return arrowFrames(state.op, state.a, state.b).map((f, i) => ({
          from: f.from,
          to: f.to,
          className: i === 0 ? 'arrow-a' : 'arrow-b',
        }));
      }

      // ── DOM: Panel (built once) ──

      // Op-selector row (3 bespoke buttons)
      const OPS: LineOp[] = ['+', '-', '*'];
      const opRow = document.createElement('div');
      opRow.className = 'arith-op-row';
      const opButtons = new Map<LineOp, HTMLButtonElement>();
      for (const op of OPS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'arith-op-btn';
        btn.textContent = OP_SYMBOL[op];
        btn.addEventListener('click', () => {
          controls.setState((s) => ({ ...s, op }));
        });
        opButtons.set(op, btn);
        opRow.appendChild(btn);
      }

      // Advanced-only operand inputs
      let inputA: HTMLInputElement | null = null;
      let inputB: HTMLInputElement | null = null;

      function makeOperandInput(which: 'a' | 'b'): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-frac-input';
        const label = document.createElement('label');
        label.textContent = which.toUpperCase();
        const input = document.createElement('input');
        input.type = 'number';
        input.step = '1';
        input.value = which === 'a' ? '2' : '3';
        input.addEventListener('change', () => {
          const v = Math.floor(Number(input.value));
          if (Number.isFinite(v)) {
            controls.setState((s) => ({ ...s, [which]: v }));
          }
        });
        wrap.appendChild(label);
        wrap.appendChild(input);
        if (which === 'a') inputA = input;
        else inputB = input;
        return wrap;
      }

      const inputAWrap = makeOperandInput('a');
      const inputBWrap = makeOperandInput('b');

      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced',
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_STORAGE_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      ctx.panel.appendChild(opRow);
      ctx.panel.appendChild(inputAWrap);
      ctx.panel.appendChild(inputBWrap);
      ctx.panel.appendChild(advToggle.element);

      // ── DOM: Action bar (built once with static preset buttons) ──
      const actionBar = createActionBar(
        ctx.actionBar,
        NL_PRESETS.map((p) => ({
          id: p.id,
          labelKey: `${p.a} ${OP_SYMBOL[p.op]} ${p.b}`,
          active: false,
          onClick() {
            controls.setState((s) => ({ ...s, a: p.a, b: p.b, op: p.op }));
          },
        })),
      );

      // ── RenderHandle ──
      function update(state: State): void {
        // Rebuild number line from state (destroy previous handle first)
        numberLineHandle?.destroy();
        const result = compute(state);
        const range = fitRange([state.a, state.b, result]);
        const points = [
          { id: 'a', value: state.a, className: 'point-a', draggable: false },
          { id: 'b', value: state.b, className: 'point-b', draggable: false },
          { id: 'r', value: result, className: 'point-result', draggable: false },
        ];
        numberLineHandle = createNumberLine(lineGroup, {
          range,
          ticks: { step: niceStep(range[1] - range[0]) },
          snap: 'integer',
          points,
          arrows: buildArrows(state),
          width: 760,
          height: 160,
        });

        // Reflect active op button
        for (const [op, btn] of opButtons) {
          btn.classList.toggle('active', op === state.op);
        }

        // Formula readout
        formula.textContent = `${state.a} ${OP_SYMBOL[state.op]} ${state.b} = ${result}`;

        // Guarded input value sync — skip when input is actively focused
        if (inputA !== null && inputA !== document.activeElement) {
          const next = String(state.a);
          if (inputA.value !== next) inputA.value = next;
        }
        if (inputB !== null && inputB !== document.activeElement) {
          const next = String(state.b);
          if (inputB.value !== next) inputB.value = next;
        }

        // Show/hide advanced operand inputs
        inputAWrap.style.display = state.advanced ? '' : 'none';
        inputBWrap.style.display = state.advanced ? '' : 'none';

        // Refresh preset-button active highlights in the action bar
        actionBar.setButtons(
          NL_PRESETS.map((p) => ({
            id: p.id,
            labelKey: `${p.a} ${OP_SYMBOL[p.op]} ${p.b}`,
            active: p.a === state.a && p.b === state.b && p.op === state.op,
            onClick() {
              controls.setState((s) => ({ ...s, a: p.a, b: p.b, op: p.op }));
            },
          })),
        );
      }

      return {
        update,
        dispose() {
          numberLineHandle?.destroy();
          numberLineHandle = null;
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Re-export as the legacy mount alias so tests and index.ts keep working
// without touching main.ts or package.json.
export const mountNumberLineSurgeon = experiment.mount.bind(experiment);
